import { Box } from "@mui/material";
import { DotLegalButton, DotLegalHeader, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { getEnumValues } from "../../../../enumOperations.ts";
import { EvaluationStatus, getEvaluationIcon } from "../EvaluateQuestionnaireDialog.types.ts";
import React, { MutableRefObject } from "react";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../../../stringOperations.ts";
import { useTranslation } from "../../../../../localization/useTranslation.ts";
import MentionInput from "../../../mentionInput/MentionInput.tsx";
import { EntityType } from "../../../../Common.types.ts";

export interface EvaluationQuestionnaireContentProps {
    evaluationStatus: EvaluationStatus | undefined;
    evaluation: string | undefined;
    info?: string;
    debounce?: boolean;
    inputRef?: MutableRefObject<HTMLTextAreaElement | undefined>;

    onEvaluationStateChange: (status: EvaluationStatus) => void;
    onEvaluationChange: (evaluation: string) => void;
    disableAutoFocus?: boolean;
    useMentionInput?: boolean;
    entityId?: string;
    entityType?: EntityType;
}

function EvaluationQuestionnaireContent(props: EvaluationQuestionnaireContentProps) {
    const { translateString, translateEvaluationEnum } = useTranslation();

    function getEvaluationStatus(status: EvaluationStatus) {
        return (
            <Box
                key={status}
                sx={(theme) => ({
                    paddingTop: 2.5,
                    width: 125,

                    backgroundColor: status === props.evaluationStatus ? theme.palette.primary.light : "white",
                    border: `2px solid ${status === props.evaluationStatus ? theme.palette.primary.main : "white"}`,
                    borderRadius: 2,
                    "&:hover": {
                        cursor: "pointer",
                    },
                })}
                onClick={() => props.onEvaluationStateChange(status)}
            >
                <Box sx={{ display: "flex", justifyContent: "center", "& svg": { height: 30 } }}>{getEvaluationIcon(status)}</Box>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 1 }}>
                    <DotLegalHeader center headerStyle={"small"}>
                        {translateEvaluationEnum(status.toString())}
                    </DotLegalHeader>
                </Box>
            </Box>
        );
    }

    return (
        <>
            {!isNullOrWhitespace(props.info) && (
                <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 1 }}>
                    <DotLegalHeader center headerStyle={"small"} fontWeight={400}>
                        {props.info}
                    </DotLegalHeader>
                </Box>
            )}

            <Box sx={{ margin: 2, display: "flex", justifyContent: "space-between" }}>{getEnumValues(EvaluationStatus).map((x) => getEvaluationStatus(x))}</Box>

            {props.useMentionInput ? (
                <MentionInput
                    value={props.evaluation ?? ""}
                    onChange={(value) => props.onEvaluationChange(value)}
                    mentionSource={{ entityFromId: props.entityId!, entityFromType: props.entityType! }}
                    rows={5}
                    multiline
                    label={translateString("evaluation")}
                    debounce={props.debounce ?? false}
                    noMargin
                    initialTextFieldFocus={!props.disableAutoFocus}
                />
            ) : (
                <DotLegalTextField
                    inputRef={props.inputRef}
                    multiline
                    noMargin
                    rows={5}
                    label={translateString("evaluation")}
                    onChange={props.onEvaluationChange}
                    value={props.evaluation ?? ""}
                    debounce={props.debounce ?? false}
                    autoFocus={!props.disableAutoFocus}
                />
            )}
        </>
    );
}

export default EvaluationQuestionnaireContent;
